import { Button, Grid, GridLabels, GridRow } from '@mm/components';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TableLabel } from '../../../../../../../common/components/TableGrid/TableGrid';
import { FacebookLoginAndConnectButton } from '../../../../../../../modules/permissions/ConnectPageWarning/ConnectPageWarning';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import { ReactComponent as MetaLogo } from 'assets/meta-logo-no-background.svg';
import styles from '../XRayAnalytics.module.css';
import css from './RestoreMetaAnalytics.module.css';
import { useAdAudienceCRUDActions } from '@mm/state/actions/AdAudienceCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../../../contacts/components/ContactsTable/ContactTable';
import {
  CellProps,
  renderAdAccountCell,
  renderAudienceCreatedDate,
  renderAudienceEmailRowsSent,
  renderAudienceSizeCell,
  renderAudienceTotalRowsSent,
  renderAudienceVisitorRowsSent,
} from '../../../Integrations/NativeIntegrations/components/FacebookAdsIntegrations/FacebookAdsIntegrations';

export const RestoreMetaAnalyticsEmptyState = ({
  button = 'Create Facebook Audience',
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const hasConnectedRealFacebookPage = useSelector(
    hasConnectedRealFacebookPageSelector,
  );
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const history = useHistory();
  const { retrieveAll } = useAdAudienceCRUDActions();

  const navigateToRestore = async () => {
    history.push(BotBuilderRoutes.restore.getLink({ chatbotId }), {
      options: 'facebook_ads_audience',
    });
  };

  useEffect(() => {
    retrieveAll(facebookPageId);
  }, []);

  return (
    <div className={css.emptyContainer}>
      {!hasConnectedRealFacebookPage ? (
        <FacebookLoginAndConnectButton
          className={css.loginButton}
          destinationPageId={facebookPageId}
        />
      ) : (
        <Button
          css={{ container: css.loginButton }}
          onClick={navigateToRestore}
        >
          {button}
        </Button>
      )}
    </div>
  );
};

export const RestoreMetaAnalytics = ({ extended }: { extended?: boolean }) => {
  const adAudiences = useSelector(
    (state: StoreState) => state.adAudiences.entities,
  );

  const cells: (ComponentDataItem & { widthPercentage: number })[] = [
    {
      label: (
        <span className={styles.labelNode}>
          <span className={styles.metaLogo}>
            <MetaLogo />{' '}
          </span>
          Restore Meta Audiences <span className={styles.note}>(All time)</span>
        </span>
      ),
      keyName: 'facebook_ad_account',
      Cell: ({ item }: CellProps) => renderAdAccountCell(item),
      widthPercentage: extended ? 40 : 80,
    },
    ...(extended
      ? [
          {
            label: 'Total Rows Sent',
            keyName: 'total_rows_sent',
            Cell: renderAudienceTotalRowsSent,
            widthPercentage: 20,
          },
          {
            label: 'Created',
            keyName: 'created',
            Cell: renderAudienceCreatedDate,
            widthPercentage: 20,
          },
        ]
      : []),
    {
      label: 'Audience Size',
      keyName: 'audience_size',
      Cell: renderAudienceSizeCell,
      widthPercentage: 20,
    },
  ];

  const gridTemplateColumns = cells.map(v => `${v.widthPercentage}%`).join(' ');

  return (
    <Grid className={classNames(styles.table, 'integrations-grid')}>
      <>
        <GridLabels
          className={classNames(styles.tableHeader, css.tableHeader)}
          gridTemplateColumns={gridTemplateColumns}
        >
          {cells.map(cell => (
            <TableLabel
              key={(cell.keyName as string) || cell.label?.toString()}
              dataItem={cell}
              orderByAtTheEnd
            />
          ))}
        </GridLabels>

        {adAudiences.length ? (
          <div className={styles.tableRows}>
            {adAudiences.map(item => (
              <GridRow
                key={item.id}
                gridTemplateColumns={gridTemplateColumns}
                className={styles.gridRow}
              >
                {cells.map(({ Cell, keyName }) =>
                  Cell ? (
                    <Cell key={keyName} item={item} />
                  ) : (
                    <DefaultCell
                      key={keyName}
                      keyName={keyName as string}
                      item={item}
                    />
                  ),
                )}
              </GridRow>
            ))}
          </div>
        ) : (
          <RestoreMetaAnalyticsEmptyState />
        )}
      </>
    </Grid>
  );
};
